import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { cn } from '../lib/utils';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { Bell, BellOff, Share2, Download, Lock, Sparkles } from 'lucide-react';
import InstallAppPrompt from '../components/shared/InstallAppPrompt';
import { isNativeApp } from '../lib/platform';
import { trackInstallEvent } from '../lib/analytics';

const API = process.env.REACT_APP_BACKEND_URL;

const getPublicGame = async (gameId) => {
  const res = await fetch(`${API}/api/games/public/${gameId}?t=${Date.now()}`, { cache: 'no-store' });
  if (!res.ok) throw new Error('Game not found');
  return res.json();
};

const getTeam = async (teamId) => {
  const res = await fetch(`${API}/api/teams/public/${teamId}?t=${Date.now()}`, { cache: 'no-store' });
  if (!res.ok) throw new Error('Team not found');
  return res.json();
};

export default function LiveScorePage() {
  const { teamId, gameId } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [viewerName, setViewerName] = useState(() => localStorage.getItem('dmp_live_viewer') || '');
  const [showNameInput, setShowNameInput] = useState(false);
  const [forecast, setForecast] = useState(null);
  const [pwaPrompt, setPwaPrompt] = useState(null);
  const [pwaInstalled, setPwaInstalled] = useState(false);
  const [teamPremium, setTeamPremium] = useState(null); // null = loading, true/false = resolved
  const { supported: pushSupported, subscription: pushSub, subscribe: pushSubscribe, unsubscribe: pushUnsubscribe } = usePushNotifications();

  // Fetch the team coach's premium status (public endpoint) to gate push signup
  useEffect(() => {
    if (!teamId) return;
    trackInstallEvent('page_view', { page: 'live_score', teamId });
    fetch(`${API}/api/teams/${teamId}/premium-status`, { cache: 'no-store' })
      .then(r => r.ok ? r.json() : null)
      .then(d => setTeamPremium(!!d?.is_premium))
      .catch(() => setTeamPremium(false));
  }, [teamId]);

  useEffect(() => {
    if (gameId && teamId) loadData();
    const interval = setInterval(loadData, 8000);
    return () => clearInterval(interval);
  }, [gameId, teamId]);

  // Capture the PWA install prompt so we can trigger it later
  useEffect(() => {
    const onBip = (e) => { e.preventDefault(); setPwaPrompt(e); };
    const onInstalled = () => { setPwaInstalled(true); setPwaPrompt(null); };
    window.addEventListener('beforeinstallprompt', onBip);
    window.addEventListener('appinstalled', onInstalled);
    // Detect already-installed (standalone display mode)
    if (window.matchMedia && window.matchMedia('(display-mode: standalone)').matches) setPwaInstalled(true);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBip);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  // Dynamic document title + meta tags so direct browser opens (and some crawlers) see live score
  useEffect(() => {
    if (!game || !team) return;
    const isHome2 = game.is_home;
    const h = game.home_score || 0;
    const a = game.away_score || 0;
    const inn = game.current_inning || 1;
    const top = game.is_top_inning !== false;
    const state = game.is_complete ? 'FINAL' : game.is_canceled ? 'CANCELED' : `${top ? 'Top' : 'Bot'} ${inn}`;
    const homeTeam = isHome2 ? team.name : game.opponent_name;
    const awayTeam = isHome2 ? game.opponent_name : team.name;
    const title = `${homeTeam} ${h}, ${awayTeam} ${a} · ${state}`;
    document.title = title;
    const setMeta = (selector, content) => {
      let el = document.head.querySelector(selector);
      if (!el) {
        el = document.createElement('meta');
        // Parse selector like 'meta[property="og:title"]' or 'meta[name="description"]'
        const match = selector.match(/meta\[(\w+)="([^"]+)"\]/);
        if (match) {
          el.setAttribute(match[1], match[2]);
        }
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };
    const desc = game.is_complete ? 'Final score' : (game.is_canceled ? 'Game canceled' : 'LIVE — tap to watch');
    setMeta('meta[name="description"]', `${team.name} vs ${game.opponent_name} — ${desc}`);
    setMeta('meta[property="og:title"]', title);
    setMeta('meta[property="og:description"]', `${team.name} vs ${game.opponent_name} — ${desc}`);
    setMeta('meta[name="twitter:title"]', title);
    setMeta('meta[name="twitter:description"]', `${team.name} vs ${game.opponent_name} — ${desc}`);
  }, [game?.home_score, game?.away_score, game?.current_inning, game?.is_top_inning, game?.is_complete, game?.is_canceled, team?.name, game?.opponent_name]);

  // Fetch forecast once we know the team's saved weather location and the game date
  useEffect(() => {
    if (!team?.weather_lat || !team?.weather_lon || !game?.date) { setForecast(null); return; }
    if (game.is_complete) { setForecast(null); return; }
    fetch(`${API}/api/weather?lat=${team.weather_lat}&lon=${team.weather_lon}`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data?.daily) return;
        import('../lib/weatherCodes').then(({ lookupForecast }) => {
          setForecast(lookupForecast({
            dates: data.daily.time || [],
            probs: data.daily.precipitation_probability_max || [],
            codes: data.daily.weather_code || [],
            tmax: data.daily.temperature_2m_max || [],
            tmin: data.daily.temperature_2m_min || [],
          }, game.date));
        });
      })
      .catch(() => setForecast(null));
  }, [team?.weather_lat, team?.weather_lon, game?.date, game?.is_complete]);

  const loadData = async () => {
    try {
      const [gameData, teamData] = await Promise.all([
        getPublicGame(gameId),
        getTeam(teamId)
      ]);
      setGame(gameData);
      setTeam(teamData);
      setLastRefresh(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!game || !team) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <p className="text-stone-500 text-2xl">Game not found</p>
      </div>
    );
  }

  const isHome = game.is_home !== false;
  const isTopInning = game.is_top_inning ?? true;
  const homeTeam = isHome ? team.name : game.opponent_name;
  const awayTeam = isHome ? game.opponent_name : team.name;
  const homeScore = game.home_score || 0;
  const awayScore = game.away_score || 0;
  const outs = game.current_outs || 0;
  const inning = game.current_inning || 1;
  const runners = game.runners_on_base || {};

  // Current batter info
  const myTeamBatting = isHome ? !isTopInning : isTopInning;
  const batterIndex = game.current_batter_index ?? 0;
  const oppBatterIndex = game.opponent_current_batter_index ?? 0;
  const batterId = myTeamBatting ? game.batting_order?.[batterIndex] : null;
  const batter = batterId ? team.players?.find(p => p.id === batterId) : null;
  const oppBatter = !myTeamBatting ? game.opponent_lineup?.[oppBatterIndex] : null;
  const batterName = myTeamBatting ? batter?.name : oppBatter?.name;
  const battingTeamName = myTeamBatting ? team.name : game.opponent_name;

  // Linescore
  const innings = game.innings || [];
  const oppInnings = game.opponent_innings || [];
  const maxInnings = Math.max(innings.length, oppInnings.length, 7);

  // Last play
  const lastPlay = game.play_log?.length > 0 ? game.play_log[game.play_log.length - 1] : null;

  return (
    <div className="min-h-screen bg-black flex flex-col items-center justify-center p-4 select-none" data-testid="live-score-page">
      {/* Live indicator */}
      <div className="flex items-center gap-2 mb-2" data-testid="live-indicator">
        {game.is_complete ? (
          <span className="text-stone-500 text-lg font-bold tracking-widest" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>FINAL</span>
        ) : (
          <>
            <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" />
            <span className="text-red-400 text-lg font-bold tracking-widest" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>LIVE</span>
          </>
        )}
      </div>

      {/* Inline game-day forecast (grandparent-friendly) */}
      {forecast && (
        <div className="flex items-center gap-2 text-stone-400 text-sm mb-4" data-testid="live-forecast">
          {(() => { const F = forecast.info.Icon; return <F size={14} className={forecast.info.color} />; })()}
          <span>{forecast.info.label}</span>
          {typeof forecast.tmax === 'number' && typeof forecast.tmin === 'number' && (
            <span className="text-stone-500">· {Math.round(forecast.tmax)}° / {Math.round(forecast.tmin)}°</span>
          )}
          {forecast.prob >= 20 && (
            <span className={forecast.prob >= 40 ? 'text-amber-400 font-semibold' : 'text-blue-400'}>
              · {forecast.prob}% rain
            </span>
          )}
        </div>
      )}

      {/* Main Scoreboard */}
      <div className="w-full max-w-xl" data-testid="live-scoreboard">
        {/* Away Team */}
        <div className={cn(
          "flex items-center justify-between px-6 py-4 rounded-t-2xl border-b border-stone-800",
          isTopInning && !game.is_complete ? "bg-stone-900 ring-1 ring-amber-500/30" : "bg-stone-900"
        )}>
          <div className="flex items-center gap-3">
            {isTopInning && !game.is_complete && (
              <div className="w-0 h-0 border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent border-l-[12px] border-l-amber-400" />
            )}
            <span className="text-stone-300 text-xl font-bold uppercase tracking-wide" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {awayTeam}
            </span>
          </div>
          <span className="text-white text-6xl font-black tabular-nums" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="away-score">
            {awayScore}
          </span>
        </div>

        {/* Home Team */}
        <div className={cn(
          "flex items-center justify-between px-6 py-4 rounded-b-2xl",
          !isTopInning && !game.is_complete ? "bg-stone-900 ring-1 ring-amber-500/30" : "bg-stone-900"
        )}>
          <div className="flex items-center gap-3">
            {!isTopInning && !game.is_complete && (
              <div className="w-0 h-0 border-t-[8px] border-t-transparent border-b-[8px] border-b-transparent border-l-[12px] border-l-amber-400" />
            )}
            <span className="text-stone-300 text-xl font-bold uppercase tracking-wide" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {homeTeam}
            </span>
          </div>
          <span className="text-white text-6xl font-black tabular-nums" style={{ fontFamily: 'Barlow Condensed, sans-serif' }} data-testid="home-score">
            {homeScore}
          </span>
        </div>
      </div>

      {/* Inning + Outs + Diamond */}
      <div className="flex items-center gap-8 mt-6" data-testid="game-state">
        {/* Inning */}
        <div className="text-center">
          <p className="text-amber-400 text-3xl font-black" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {isTopInning ? 'TOP' : 'BOT'} {inning}
          </p>
        </div>

        {/* Outs */}
        <div className="flex items-center gap-2">
          {[0, 1, 2].map(i => (
            <div key={i} className={cn(
              "w-5 h-5 rounded-full border-2 transition-colors",
              i < outs ? "bg-amber-400 border-amber-400" : "border-stone-600"
            )} />
          ))}
          <span className="text-stone-500 text-sm ml-1 font-semibold">OUT</span>
        </div>

        {/* Mini Diamond */}
        <svg viewBox="0 0 60 60" className="w-14 h-14">
          <rect x="28" y="48" width="4" height="4" fill="#fff" transform="rotate(45 30 50)" />
          <rect x="44" y="28" width="4" height="4" fill={runners.first ? '#f59e0b' : '#374151'} stroke="#555" strokeWidth="0.5" transform="rotate(45 46 30)" />
          <rect x="28" y="12" width="4" height="4" fill={runners.second ? '#f59e0b' : '#374151'} stroke="#555" strokeWidth="0.5" transform="rotate(45 30 14)" />
          <rect x="12" y="28" width="4" height="4" fill={runners.third ? '#f59e0b' : '#374151'} stroke="#555" strokeWidth="0.5" transform="rotate(45 14 30)" />
          <line x1="30" y1="50" x2="46" y2="30" stroke="rgba(255,255,255,0.15)" strokeWidth="0.5" />
          <line x1="46" y1="30" x2="30" y2="14" stroke="rgba(255,255,255,0.15)" strokeWidth="0.5" />
          <line x1="30" y1="14" x2="14" y2="30" stroke="rgba(255,255,255,0.15)" strokeWidth="0.5" />
          <line x1="14" y1="30" x2="30" y2="50" stroke="rgba(255,255,255,0.15)" strokeWidth="0.5" />
        </svg>
      </div>

      {/* At Bat */}
      {batterName && !game.is_complete && (
        <div className="mt-5 text-center" data-testid="at-bat-info">
          <p className="text-stone-600 text-xs uppercase tracking-widest">At Bat</p>
          <p className="text-white text-2xl font-bold" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
            {batterName}
          </p>
          <p className="text-stone-500 text-xs">{battingTeamName}</p>
        </div>
      )}

      {/* Linescore */}
      <div className="w-full max-w-xl mt-6 overflow-x-auto" data-testid="linescore">
        <div className="min-w-fit mx-auto">
          {/* Header */}
          <div className="flex gap-0 text-xs text-stone-600 font-mono">
            <div className="w-24 shrink-0" />
            {Array.from({ length: maxInnings }, (_, i) => (
              <div key={i} className="w-8 text-center">{i + 1}</div>
            ))}
            <div className="w-10 text-center font-bold text-stone-400">R</div>
          </div>
          {/* Away */}
          <div className="flex gap-0 text-xs font-mono">
            <div className="w-24 shrink-0 text-stone-400 truncate pr-1">{awayTeam}</div>
            {Array.from({ length: maxInnings }, (_, i) => {
              const src = isHome ? oppInnings : innings;
              const runs = src?.find(x => x.inning === i + 1)?.runs;
              return <div key={i} className="w-8 text-center text-stone-300">{runs != null ? runs : '-'}</div>;
            })}
            <div className="w-10 text-center text-amber-400 font-bold">{awayScore}</div>
          </div>
          {/* Home */}
          <div className="flex gap-0 text-xs font-mono">
            <div className="w-24 shrink-0 text-stone-400 truncate pr-1">{homeTeam}</div>
            {Array.from({ length: maxInnings }, (_, i) => {
              const src = isHome ? innings : oppInnings;
              const runs = src?.find(x => x.inning === i + 1)?.runs;
              return <div key={i} className="w-8 text-center text-stone-300">{runs != null ? runs : '-'}</div>;
            })}
            <div className="w-10 text-center text-amber-400 font-bold">{homeScore}</div>
          </div>
        </div>
      </div>

      {/* Last Play */}
      {lastPlay && !game.is_complete && (
        <div className="mt-5 w-full max-w-xl" data-testid="last-play">
          <div className={cn(
            "text-center py-2 px-4 rounded-xl text-sm",
            lastPlay.description?.includes('Home Run') || lastPlay.description?.includes('Triple') ? "bg-green-900/40 text-green-300" :
            lastPlay.description?.includes('Out') || lastPlay.description?.includes('Strikeout') ? "bg-red-900/30 text-red-400" :
            lastPlay.description?.includes('Score') || lastPlay.description?.includes('scores') ? "bg-amber-900/30 text-amber-300" :
            "bg-stone-900 text-stone-400"
          )}>
            {lastPlay.description}
          </div>
        </div>
      )}

      {/* Notify Me — gated behind coach's premium (hidden in native app builds) */}
      {!game.is_complete && pushSupported && !isNativeApp() && (
        <div className="mt-6 w-full max-w-xl" data-testid="notify-section">
          {pushSub ? (
            <button
              onClick={async () => { await pushUnsubscribe(); }}
              className="w-full flex items-center justify-center gap-2 py-3 px-4 rounded-xl bg-green-900/40 border border-green-700/40 text-green-400 text-sm font-semibold transition-colors hover:bg-green-900/60"
              data-testid="notifications-on-btn"
            >
              <Bell size={16} className="animate-pulse" /> Notifications On
              <span className="text-green-600 text-xs ml-1">(tap to turn off)</span>
            </button>
          ) : teamPremium === false ? (
            /* Coach is not on Premium — show upgrade prompt instead of push signup */
            <div
              className="w-full rounded-xl bg-gradient-to-br from-amber-900/30 to-stone-900 border border-amber-700/40 p-4 text-center space-y-2"
              data-testid="notify-premium-locked"
            >
              <div className="inline-flex items-center gap-1.5 text-amber-400 text-[11px] font-bold tracking-wider">
                <Lock size={11} /> PUSH ALERTS · PREMIUM
              </div>
              <p className="text-stone-200 text-sm font-semibold">Ask {team?.name || 'the coach'} to unlock push alerts</p>
              <p className="text-stone-400 text-xs leading-relaxed">
                When the coach subscribes to Diamond Scorer Premium, everyone following this team gets free big-play alerts — home runs, lead changes, and more.
              </p>
              <button
                onClick={async () => {
                  const shareText = `Hey coach — I'm following ${team?.name || 'the team'} on Diamond Scorer. Can you upgrade to Premium so I can get push alerts for big plays? Upgrade here: ${window.location.origin}/upgrade`;
                  try {
                    if (navigator.share) {
                      await navigator.share({ title: 'Diamond Scorer', text: shareText });
                    } else {
                      await navigator.clipboard.writeText(shareText);
                      // eslint-disable-next-line no-alert
                      alert('Message copied — paste it into a text to your coach.');
                    }
                  } catch {}
                }}
                className="w-full inline-flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-sm font-bold transition-colors"
                data-testid="ask-coach-upgrade-btn"
              >
                <Sparkles size={14} /> Ask coach to upgrade
              </button>
            </div>
          ) : !showNameInput ? (
            <button
              onClick={() => setShowNameInput(true)}
              className="w-full flex items-center justify-center gap-2 py-4 px-4 rounded-xl bg-amber-900/30 border border-amber-700/40 text-amber-400 text-base font-bold transition-colors hover:bg-amber-900/50"
              data-testid="notify-me-btn"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              <Bell size={20} /> NOTIFY ME ON BIG PLAYS
            </button>
          ) : (
            <div className="bg-stone-900 rounded-xl p-4 border border-stone-800 space-y-3" data-testid="notify-signup">
              <p className="text-stone-400 text-xs text-center">Enter your name to get notifications for scoring plays, home runs, and more</p>
              <input
                type="text"
                value={viewerName}
                onChange={e => setViewerName(e.target.value)}
                placeholder="Your name (e.g. Grandma)"
                className="w-full bg-stone-800 border border-stone-700 rounded-lg px-4 py-2.5 text-white text-sm text-center focus:outline-none focus:ring-2 focus:ring-amber-500"
                data-testid="viewer-name-input"
                autoFocus
              />
              <div className="flex gap-2">
                <button
                  onClick={() => setShowNameInput(false)}
                  className="flex-1 py-2 rounded-lg bg-stone-800 text-stone-500 text-sm"
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    if (!viewerName.trim()) return;
                    localStorage.setItem('dmp_live_viewer', viewerName.trim());
                    await pushSubscribe(teamId, [], viewerName.trim());
                    setShowNameInput(false);
                  }}
                  disabled={!viewerName.trim()}
                  className="flex-1 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-sm font-bold disabled:opacity-40 flex items-center justify-center gap-1.5"
                  data-testid="enable-notifications-btn"
                >
                  <Bell size={14} /> Enable Alerts
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Share + Install CTAs */}
      <div className="mt-6 max-w-xl mx-auto">
        <InstallAppPrompt variant="banner" />
      </div>
      <div className="mt-3 flex flex-wrap gap-2 justify-center" data-testid="live-cta-row">
        <button
          type="button"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-stone-800 hover:bg-stone-700 border border-stone-700 text-stone-200 text-xs font-semibold transition-colors"
          onClick={async () => {
            const shareUrl = `${window.location.origin}/api/share/live/${teamId}/${gameId}`;
            const isHome2 = game.is_home;
            const h = game.home_score || 0;
            const a = game.away_score || 0;
            const homeTeam = isHome2 ? team.name : game.opponent_name;
            const awayTeam = isHome2 ? game.opponent_name : team.name;
            const state = game.is_complete ? 'FINAL' : game.is_canceled ? 'CANCELED' : `${(game.is_top_inning !== false) ? 'Top' : 'Bot'} ${game.current_inning || 1}`;
            const title = `${homeTeam} ${h}, ${awayTeam} ${a} · ${state}`;
            if (navigator.share) {
              try { await navigator.share({ title, text: title, url: shareUrl }); } catch {}
            } else {
              try { await navigator.clipboard.writeText(shareUrl); alert('Link copied — score auto-updates for anyone who opens it'); } catch {}
            }
          }}
          data-testid="live-share-btn"
        >
          <Share2 size={12} /> Share live link
        </button>
        {pwaPrompt && !pwaInstalled && (
          <button
            type="button"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-700 hover:bg-emerald-600 text-white text-xs font-semibold transition-colors"
            onClick={async () => {
              try {
                pwaPrompt.prompt();
                const choice = await pwaPrompt.userChoice;
                if (choice?.outcome === 'accepted') setPwaInstalled(true);
                setPwaPrompt(null);
              } catch {}
            }}
            data-testid="live-install-btn"
          >
            <Download size={12} /> Install app for push alerts
          </button>
        )}
      </div>

      {/* Footer */}
      <div className="mt-6 text-center space-y-1">
        {lastRefresh && (
          <p className="text-stone-700 text-[10px]">
            Auto-refreshing {lastRefresh}
          </p>
        )}
        <button
          onClick={() => navigate('/')}
          className="text-stone-800 text-[10px] font-semibold hover:text-amber-500 transition-colors"
          style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          data-testid="live-score-home-link"
        >
          DIAMOND<span className="text-amber-600 italic"> SCORER</span>
          <span className="ml-2 text-stone-700">· tap to visit site</span>
        </button>
      </div>
    </div>
  );
}
